import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { acceptInvite, getAcceptInviteInfo } from "../api";
import { LoadingSpinner } from "../components/SEOComponents";
import { useT, useLang } from "../i18n";
import safeStorage from "../lib/safeStorage";
import { tx as __tx } from "../lib/tx";

const TOKEN_KEY = "xpnsr_seo_token";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AcceptInvite({ onLogin } = {}) {
  const t = useT();
  const { lang, switchLang } = useLang();
  const tx = t.acceptInvite;
  const params = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        if (alive) {
          setError(tx.invalid);
          setLoading(false);
        }
        return;
      }
      try {
        const { data } = await getAcceptInviteInfo(token);
        if (alive) setInfo(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || tx.invalid);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, tx.invalid]);

  const minLen = info?.min_password_len ?? 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < minLen) {
      toast.error(tx.minHint(minLen));
      return;
    }
    if (password !== confirm) {
      toast.error(tx.mismatch);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await acceptInvite({ token, password });
      safeStorage.setItem(TOKEN_KEY, data.token);
      toast.success(tx.success);
      if (typeof onLogin === "function") {
        onLogin(data.token);
      }
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || tx.invalid);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="xp-app xp-app-auth">
      <div className="orb orb-1" aria-hidden="true" />
      <div className="orb orb-2" aria-hidden="true" />

      <main className="xp-main" style={{ marginLeft: 0 }}>
        <div className="xp-top">
          <div className="xp-crumb">
            <span>{__tx("xpnsr")}</span>
            <span className="xp-crumb-sep">/</span>
            <b>SEO</b>
            <span className="xp-crumb-sep">/</span>
            <span>{__tx("invite")}</span>
          </div>
          <div className="xp-lang" role="group" aria-label={__tx("Language")} style={{ marginLeft: "auto" }}>
            {["en", "ru"].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => switchLang(l)}
                className={`xp-lang-btn ${lang === l ? "on" : ""}`}
                aria-pressed={lang === l}
              >
                {l === "en" ? "EN" : "RU"}
              </button>
            ))}
          </div>
        </div>

        <div className="xp-auth-wrap">
          <div className="xp-auth-card" data-testid="accept-invite-card" key={lang}>
            <div className="xp-chrome">
              <div className="xp-chrome-dots">
                <i></i>
                <i></i>
                <i></i>
              </div>
              <div className="xp-chrome-path">{__tx("auth.xpnsr.tech/seo/invite")}</div>
              <div className="xp-chrome-tls">{__tx("TLS 1.3")}</div>
            </div>

            <div className="xp-card-body">
              <h1 className="xp-h1">
                <em>{tx.title}</em>
              </h1>
              <p className="xp-auth-sub">{tx.sub}</p>

              {loading ? (
                <div
                  className="flex items-center justify-center"
                  style={{ minHeight: 180 }}
                >
                  <LoadingSpinner label={tx.loading} />
                </div>
              ) : error ? (
                <div data-testid="accept-invite-error">
                  <div className="xp-alert" role="alert" style={{ marginBottom: 12 }}>
                    <span aria-hidden="true">⚠</span> {error}
                  </div>
                  <a className="xp-btn-main" href="/">
                    {tx.invalidGoLogin}
                  </a>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  aria-label={__tx("Accept invite form")}
                >
                  <div className="xp-f">
                    <label>{tx.emailLabel}</label>
                    <input
                      type="email"
                      value={info?.email || ""}
                      readOnly
                      data-testid="accept-invite-email"
                      autoComplete="username"
                    />
                  </div>

                  {info?.expires_at ? (
                    <p
                      className="xp-auth-sub"
                      data-testid="accept-invite-expires"
                      style={{ marginTop: -6 }}
                    >
                      {tx.expiresAt(fmtDate(info.expires_at))}
                    </p>
                  ) : null}

                  <div className="xp-f">
                    <label htmlFor="invite-pw">{tx.passwordLabel}</label>
                    <input
                      id="invite-pw"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={tx.passwordHolder}
                      autoComplete="new-password"
                      data-testid="accept-invite-password"
                      minLength={minLen}
                      required
                    />
                    <span className="sr-only">{tx.minHint(minLen)}</span>
                  </div>

                  <div className="xp-f">
                    <label htmlFor="invite-pw2">{tx.confirmLabel}</label>
                    <input
                      id="invite-pw2"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder={tx.passwordHolder}
                      autoComplete="new-password"
                      data-testid="accept-invite-confirm"
                      minLength={minLen}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="xp-btn-main"
                    disabled={submitting || !password || !confirm}
                    data-testid="accept-invite-submit"
                  >
                    {submitting ? tx.submitting : `${tx.submit} →`}
                  </button>

                  <a
                    className="xp-forgot"
                    href="/"
                    data-testid="accept-invite-back"
                  >
                    {tx.backToLogin}
                  </a>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
